/**
 * La fiche d'un fournisseur (V1.6-11).
 *
 * Trois sections et rien d'autre : ce qu'on sait de lui, ce qu'il a fait, et ce
 * qu'on lui a acheté. **Ce n'est pas un CRM** — pas d'opportunités, pas de
 * relances, pas de champs à remplir pour faire joli.
 *
 * Ce que cet écran tient :
 *
 * * **aucune note globale.** Pas de « 87/100 ». Un score mélangerait un délai,
 *   un taux de réponse et un prix avec des poids que personne n'a écrits ;
 * * **inconnu n'est pas zéro.** Chaque carte passe par `MetricValue`, qui
 *   affiche un tiret plutôt qu'un faux zéro ;
 * * **chaque chiffre porte sa définition.** Quelqu'un qui voit 40 % doit
 *   pouvoir comprendre pourquoi sans lire le code ;
 * * **trois séries de prix, jamais fondues.** Coté, validé, commandé sont trois
 *   natures de fait.
 */
import { useState } from "react";
import { useParams } from "react-router-dom";

import {
  useSupplier,
  useSupplierIntelligence,
  useSupplierPriceHistory,
} from "@/api/supplier-intelligence";
import type { PriceObservation, ProductQuantity, SupplierIntelligence } from "@/api/types";
import { Card, CardBody, CardHeader } from "@/components/Card";
import { TableShell, Td, Th, Tr } from "@/components/DataTable";
import { ErrorState, TableSkeleton } from "@/components/States";
import { StatusBadge } from "@/components/StatusBadge";
import { Page } from "@/layout/Page";
import { PageHeader } from "@/layout/PageHeader";
import { formatDate, formatDateTime } from "@/lib/format";
import { MetricCard, MetricValue } from "./MetricValue";

/** Les périodes proposées. Douze mois par défaut, comme le service. */
const PERIODS: { label: string; months: number | null }[] = [
  { label: "12 derniers mois", months: 12 },
  { label: "3 derniers mois", months: 3 },
  { label: "Depuis toujours", months: null },
];

/** Ce que chaque série de prix raconte. Les fondre ferait une fausse courbe. */
const PRICE_SOURCES: Record<string, string> = {
  QUOTE: "Coté",
  PRODUCT_PRICE: "Validé",
  PURCHASE_ORDER: "Commandé",
};

function since(months: number | null): { date_from?: string } {
  if (months === null) return {};
  const from = new Date();
  from.setMonth(from.getMonth() - months);
  return { date_from: from.toISOString() };
}

export function SupplierDetailPage() {
  const { supplierId } = useParams<{ supplierId: string }>();
  const [months, setMonths] = useState<number | null>(12);
  const supplier = useSupplier(supplierId);
  const metrics = useSupplierIntelligence(supplierId, since(months));
  const prices = useSupplierPriceHistory(supplierId);

  if (supplier.isLoading) {
    return (
      <Page>
        <TableSkeleton rows={6} columns={4} />
      </Page>
    );
  }
  if (supplier.error || !supplier.data) {
    return (
      <Page>
        <ErrorState error={supplier.error} onRetry={() => void supplier.refetch()} />
      </Page>
    );
  }

  const found = supplier.data;
  return (
    <Page>
      <PageHeader
        title={found.name}
        subtitle={found.contact_name ?? undefined}
        crumbs={[{ label: "Fournisseurs", to: "/suppliers" }, { label: found.name }]}
        actions={
          found.active ? (
            <StatusBadge tone="available">Actif</StatusBadge>
          ) : (
            <StatusBadge tone="out">Retiré</StatusBadge>
          )
        }
      />

      <div className="grid gap-4 @4xl:grid-cols-[20rem_minmax(0,1fr)]">
        <Card>
          <CardHeader title="Coordonnées" />
          <CardBody>
            <dl className="space-y-2 text-[13px]">
              <Field label="Contact" value={found.contact_name} />
              <Field label="Courriel" value={found.email} />
              <Field label="Téléphone" value={found.phone} />
              <Field label="Numéro de compte" value={found.account_number} />
              <Field label="Site" value={found.website} />
              {found.notes && <Field label="Notes" value={found.notes} />}
            </dl>
          </CardBody>
        </Card>

        <div className="min-w-0 space-y-4">
          <div className="flex flex-wrap items-center gap-1.5">
            {PERIODS.map((item) => (
              <button
                key={item.label}
                type="button"
                onClick={() => setMonths(item.months)}
                aria-pressed={months === item.months}
                className={`rounded-md border px-2.5 py-1 text-[12px] ${
                  months === item.months
                    ? "border-brand/40 bg-brand/10 text-brand"
                    : "border-border text-ink-muted hover:text-ink"
                }`}
              >
                {item.label}
              </button>
            ))}
          </div>

          {metrics.isLoading && <TableSkeleton rows={4} columns={4} />}
          {metrics.error && (
            <ErrorState error={metrics.error} onRetry={() => void metrics.refetch()} />
          )}
          {metrics.data && <Performance data={metrics.data} />}
          <PriceHistory
            observations={prices.data ?? []}
            loading={prices.isLoading}
          />
        </div>
      </div>
    </Page>
  );
}

function Field({ label, value }: { label: string; value: string | null }) {
  return (
    <div>
      <dt className="text-ink-subtle">{label}</dt>
      <dd className="text-ink">{value ?? "—"}</dd>
    </div>
  );
}

/* ------------------------------------------ historique & performance */

function Performance({ data }: { data: SupplierIntelligence }) {
  return (
    <section aria-labelledby="performance-heading" className="min-w-0 space-y-3">
      <div>
        <h2 id="performance-heading" className="text-[15px] font-semibold text-ink">
          Historique &amp; performance
        </h2>
        <p className="text-[12px] text-ink-subtle">
          Des faits mesurés, pas une note. Un tiret veut dire « on ne sait pas », jamais
          « zéro ».
        </p>
      </div>

      <div className="grid min-w-0 gap-3 @2xl:grid-cols-2 @5xl:grid-cols-3">
        <MetricCard
          label="Taux de réponse"
          metric={data.rfq_response_rate}
          kind="rate"
          sampleLabel="demandes envoyées"
          explanation="Demandes auxquelles il a répondu, sur les demandes réellement transmises. Une demande faite au téléphone ne compte pas."
        />
        <MetricCard
          label="Délai de réponse (médiane)"
          metric={data.median_response_hours}
          kind="hours"
          sampleLabel="réponses datées"
          explanation="Entre l'envoi de la demande et la première soumission reçue. Les couples sans les deux horodatages sortent de l'échantillon."
        />
        <MetricCard
          label="Remplissage brut"
          metric={data.gross_fill_rate}
          kind="rate"
          sampleLabel="lignes de commande"
          explanation="Moyenne du pourcentage servi sur chaque ligne de commande. Chaque ligne compte pour une, quelle que soit sa taille, et une sur-réception ne dépasse pas 100 % sur la sienne."
        />
        <MetricCard
          label="Complétion effective"
          metric={data.effective_completion_rate}
          kind="rate"
          sampleLabel="lignes de commande"
          explanation="La même moyenne, après avoir retiré de chaque ligne ce qu'on a explicitement abandonné. Dit si l'affaire est close, pas si le fournisseur a livré."
        />
        <MetricCard
          label="Délai réel (1re réception)"
          metric={data.average_first_receipt_lead_days}
          kind="days"
          sampleLabel="commandes reçues"
          explanation="Entre la date de commande et la première réception physique. Distinct du délai annoncé en soumission."
        />
        <MetricCard
          label="Ponctualité"
          metric={data.on_time_delivery_rate}
          kind="rate"
          sampleLabel="commandes avec date promise"
          explanation="Reçu au plus tard à la date attendue, tolérance zéro jour. Une commande sans date promise n'est ni à l'heure ni en retard : elle sort du calcul."
        />
        <MetricCard
          label="Moins cher connu"
          metric={data.cheapest_known_rate}
          kind="rate"
          sampleLabel="comparaisons comparables"
          explanation="Sur les demandes où au moins deux fournisseurs ont chiffré le même article, dans la même devise et une unité convertible. À égalité, tous comptent."
        />
      </div>

      <div className="grid min-w-0 gap-3 @2xl:grid-cols-2">
        <Card>
          <CardHeader title="Demandes et soumissions" />
          <CardBody className="space-y-1.5 text-[13px]">
            <Line label="Demandes envoyées" value={data.rfqs_requested} />
            <Line label="Demandes avec réponse" value={data.rfqs_responded} />
            <Line
              label="Documents de soumission reçus"
              value={data.quotes_received}
              hint="Trois révisions font trois documents et une seule réponse."
            />
            <Line label="Lignes chiffrées" value={data.quote_lines_received} />
            <Line
              label="Substitutions proposées"
              value={data.substitutions_proposed}
              hint="Proposées, pas acceptées : le lignage vers la commande ne le prouve pas."
            />
          </CardBody>
        </Card>

        <Card>
          <CardHeader title="Commandes et réceptions" />
          <CardBody className="space-y-1.5 text-[13px]">
            <Line label="Commandes passées" value={data.purchase_orders} />
            <Line label="Lignes commandées" value={data.ordered_lines} />
            <Line label="Lignes reçues" value={data.received_lines} />
            <Line
              label="Closes par abandon"
              value={data.closed_by_abandonment}
              hint="Ce ne sont pas des livraisons complètes ; le délai de complétion les exclut."
            />
            <Quantities label="Encore attendu" rows={data.backordered_by_product} />
            <Quantities label="Abandonné" rows={data.abandoned_by_product} />
            <Quantities label="Renvoyé" rows={data.returned_by_product} />
            {data.ordered_value_known.length > 0 && (
              <div className="pt-1">
                <p className="text-[11px] uppercase tracking-wide text-ink-subtle">
                  Valeur commandée connue
                </p>
                {data.ordered_value_known.map((money) => (
                  <p key={money.currency} className="tnum text-[13px] text-ink">
                    {money.amount} {money.currency}{" "}
                    <span className="text-[11px] text-ink-subtle">
                      sur {money.line_count} lignes chiffrées
                    </span>
                  </p>
                ))}
              </div>
            )}
          </CardBody>
        </Card>
      </div>

      {(data.returns_by_reason.length > 0 || data.abandonment_by_reason.length > 0) && (
        <div className="grid min-w-0 gap-3 @2xl:grid-cols-2">
          <Reasons
            title="Retours fournisseur"
            subtitle="Un retour n'est pas un défaut : on renvoie aussi ce qu'on a commandé en trop."
            rows={data.returns_by_reason}
            total={data.supplier_returns_count}
          />
          <Reasons
            title="Reliquats abandonnés"
            subtitle="« Le fournisseur a annulé » et « on n'en avait plus besoin » ne racontent pas la même histoire."
            rows={data.abandonment_by_reason}
          />
        </div>
      )}
    </section>
  );
}

function Line({
  label,
  value,
  hint,
}: {
  label: string;
  value: number | string;
  hint?: string;
}) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="min-w-0">
        <span className="text-ink-muted">{label}</span>
        {hint && <span className="block text-[11px] text-ink-subtle">{hint}</span>}
      </span>
      <span className="tnum shrink-0 font-medium text-ink">{value}</span>
    </div>
  );
}

/**
 * Une quantité physique, **ventilée par produit**.
 *
 * Jamais un total. Un fournisseur qui vend des vis à la pièce et du câble au
 * mètre n'a pas « 190 en attente » : il a cent pièces et quatre-vingt-dix
 * mètres, et les additionner donnerait un nombre qui ne décrit aucun objet.
 */
function Quantities({ label, rows }: { label: string; rows: ProductQuantity[] }) {
  if (rows.length === 0) return null;
  return (
    <div className="pt-1">
      <p className="text-[11px] uppercase tracking-wide text-ink-subtle">{label}</p>
      <ul className="mt-0.5 space-y-0.5">
        {rows.map((row) => (
          <li
            key={`${row.product_id}-${row.unit}`}
            className="flex items-baseline justify-between gap-3 text-[12px]"
          >
            <span className="min-w-0 truncate text-ink-muted">
              {row.product_name ?? "Article"}
            </span>
            <span className="tnum shrink-0 text-ink">
              {row.quantity} {row.unit}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Reasons({
  title,
  subtitle,
  rows,
  total,
}: {
  title: string;
  subtitle: string;
  rows: { reason: string; count: number }[];
  total?: number;
}) {
  return (
    <Card>
      <CardHeader title={title} subtitle={subtitle} />
      <CardBody>
        {rows.length === 0 ? (
          <p className="text-[13px] text-ink-subtle">Aucun sur cette période.</p>
        ) : (
          <ul className="space-y-1 text-[13px]">
            {rows.map((row) => (
              <li key={row.reason} className="flex items-baseline justify-between gap-3">
                <span className="text-ink-muted">{row.reason}</span>
                <span className="tnum text-ink">{row.count}</span>
              </li>
            ))}
          </ul>
        )}
        {total !== undefined && rows.length > 0 && (
          <p className="mt-2 text-[11px] text-ink-subtle">{total} retours au total.</p>
        )}
      </CardBody>
    </Card>
  );
}

/* ------------------------------------------------ produits & prix */

function PriceHistory({
  observations,
  loading,
}: {
  observations: PriceObservation[];
  loading: boolean;
}) {
  return (
    <Card>
      <CardHeader
        title="Produits &amp; prix"
        subtitle="Trois séries distinctes : ce qu'il a coté, ce qu'on a validé, ce qu'on a commandé. Elles ne se fondent pas."
      />
      {loading && <TableSkeleton rows={4} columns={5} />}
      {!loading && observations.length === 0 && (
        <CardBody>
          <p className="text-[13px] text-ink-subtle">Aucun prix observé chez ce fournisseur.</p>
        </CardBody>
      )}
      {!loading && observations.length > 0 && (
        <div className="hidden md:block">
          <TableShell>
            <thead>
              <tr>
                <Th className="whitespace-nowrap">Date</Th>
                <Th>Source</Th>
                <Th align="right">Prix</Th>
                <Th>Devise</Th>
                <Th>Unité</Th>
                <Th className="hidden max-w-0 @4xl:table-cell">Conditionnement</Th>
              </tr>
            </thead>
            <tbody>
              {observations.map((item) => (
                <Tr key={`${item.source_type}-${item.source_id}`}>
                  <Td className="whitespace-nowrap text-[12px] text-ink-subtle">
                    {formatDate(item.observed_at)}
                  </Td>
                  <Td className="text-[13px] text-ink-muted">
                    {PRICE_SOURCES[item.source_type] ?? item.source_type}
                  </Td>
                  <Td align="right" className="tnum text-[13px] text-ink">
                    {item.amount}
                  </Td>
                  <Td className="text-[13px] text-ink-muted">{item.currency ?? "—"}</Td>
                  <Td className="text-[13px] text-ink-muted">{item.unit ?? "—"}</Td>
                  <Td className="hidden max-w-0 truncate text-[12px] text-ink-subtle @4xl:table-cell">
                    {item.package_description ?? "—"}
                  </Td>
                </Tr>
              ))}
            </tbody>
          </TableShell>
        </div>
      )}
      {!loading && observations.length > 0 && (
        <ul className="divide-y divide-border/60 md:hidden">
          {observations.map((item) => (
            <li key={`${item.source_type}-${item.source_id}`} className="px-3 py-2.5">
              <div className="flex items-baseline justify-between gap-2">
                <span className="text-[13px] text-ink-muted">
                  {PRICE_SOURCES[item.source_type] ?? item.source_type}
                </span>
                <span className="tnum text-[13px] text-ink">
                  {item.amount} {item.currency ?? ""}
                </span>
              </div>
              <p className="text-[11px] text-ink-subtle">
                {formatDateTime(item.observed_at)}
                {item.unit ? ` · ${item.unit}` : ""}
              </p>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

export { MetricValue };
